import { MigrationEngine } from '@framework/engine/migrations/MigrationEngine.ts';
import { type IRootWidget } from '@framework/engine/schema/widgets/repository/containers/root/IRootWidget.ts';
import { ReferenceMigration002ActiveUserLabels } from '../references/migrations/ReferenceMigration002ActiveUserLabels.ts';
import { MapWidgetMigration001UnifiedLayerModel } from '../widgets/map/migrations/MapWidgetMigration001UnifiedLayerModel.ts';

/**
 * Engine di migrazione per le sezioni.
 *
 * Opera sulla IRootWidget (radice del widget tree). Il versionId è
 * salvato direttamente sulla root, in modo da essere indipendente
 * dal tipo di DTO utilizzato (FO, BO, Admin).
 *
 * Ogni MigrationFunction<IRootWidget> può camminare il tree con
 * TreeNavigatorLogic.forEachNode per trovare e migrare widget specifici.
 *
 * ## Trigger
 *
 * Le migrazioni vengono eseguite automaticamente al caricamento
 * delle sezioni, prima del rendering:
 * - FO: `useSections` (dentro `produce()`, prima delle traduzioni)
 * - BO: `mapDeclarationSections` (dentro `produce()`)
 * - BO Task: `useDeclarations` (via `migrateDeclarationSection`,
 *   dopo il remap readonly)
 * - Procedimento: `ProcedureDeclarationSection` (dentro `produce()`)
 * - Admin: `mapSectionToSchemaValue` (dentro `produce()`,
 *   al caricamento della sezione in `useSection`)
 * - Validator API: `EngineValidator.convertSectionsToEngine`
 *   (dentro `produce()`, punto unico per validazione modulo,
 *   sezioni dati e idratazione)
 */
export class SectionMigrationEngine extends MigrationEngine<IRootWidget> {
  getVersionId(data: IRootWidget): number {
    return data.versionId ?? 0;
  }

  setVersionId(data: IRootWidget, versionId: number): void {
    data.versionId = versionId;
  }
}

/**
 * Istanza globale del motore di migrazione sezioni.
 *
 * Le migrazioni vanno aggiunte all'array in ordine sequenziale
 * (versionId 1, 2, 3, …). Il costruttore valida l'ordine.
 *
 * @example
 * ```ts
 * import { Migration001 } from './widgets/map/migrations/Migration001.ts';
 * import { Migration002 } from './widgets/foo/migrations/Migration002.ts';
 *
 * export const sectionMigrationEngine = new SectionMigrationEngine([
 *   new Migration001(),
 *   new Migration002(),
 * ]);
 * ```
 */
export const sectionMigrationEngine = new SectionMigrationEngine([
  new MapWidgetMigration001UnifiedLayerModel(),
  new ReferenceMigration002ActiveUserLabels(),
]);
